import api, { ApiResponse } from './api';
import type {
  AgencyLinkLookupResponse,
  AgencyLinkOutcome,
  AgencyLinkRequestDetailResponse,
  AgencyLinkRequestsResponse,
  AgencyLinkRequestStatus,
  ApproveAgencyLinkRequestResponse,
  ConnectAgencyLinkRequest,
  CreateAgencySiteRequest,
  ResolveAgencyLinkRequestResponse,
  SiteAgencyLinksResponse,
  UnlinkAgencyLinkResponse,
} from '@safetyhub/shared';

/**
 * 대행 현장 연결·해제·요청(P4) API 클라이언트 — 계약 정본은 shared `features/agency-site-link.ts`.
 *
 * base `/organizations/:organizationId` · 호출 조직 = 현재 작업공간. (종전의 피처 플래그 `agency-site-link` 게이트는 2026-11-01에
 * 걷었다 — 입구는 권한만 본다.) 쿼리 키 접두 `'agency-site-link'`는 캐시 이름일 뿐 플래그가 아니다.
 * 순수 술어·경로는 `lib/agencySiteLinkView.ts`(이 모듈과 갈라 둔 이유는 그 파일 머리말).
 */

const base = (organizationId: string) => `/organizations/${encodeURIComponent(organizationId)}`;

export const agencySiteLinkQueryKeys = {
  all: ['agency-site-link'] as const,
  lookup: (organizationId: string, registrationNumber: string) =>
    ['agency-site-link', 'lookup', organizationId, registrationNumber] as const,
  siteLinks: (organizationId: string, siteId: string) =>
    ['agency-site-link', 'site-links', organizationId, siteId] as const,
  requests: (organizationId: string, direction: 'incoming' | 'outgoing', status: string) =>
    ['agency-site-link', 'requests', organizationId, direction, status] as const,
  request: (organizationId: string, requestId: string) =>
    ['agency-site-link', 'request', organizationId, requestId] as const,
};

export const agencySiteLinkApi = {
  /** 사업자등록번호로 업체·현장 조회(기관 · `site.manage`) — 조회 자체가 감사 로그에 남는다 */
  lookup: async (organizationId: string, registrationNumber: string): Promise<AgencyLinkLookupResponse> => {
    const response = await api.get<ApiResponse<AgencyLinkLookupResponse>>(
      `${base(organizationId)}/agency-links/lookup?registrationNumber=${encodeURIComponent(registrationNumber)}`,
    );
    return response.data.data!;
  },

  /** 기존 현장 연결(기관) — 갈래에 따라 연결됐거나(`CONNECTED`) 요청됐다(`REQUESTED`) */
  connect: async (organizationId: string, body: ConnectAgencyLinkRequest): Promise<AgencyLinkOutcome> => {
    const response = await api.post<ApiResponse<AgencyLinkOutcome>>(`${base(organizationId)}/agency-links`, body);
    return response.data.data!;
  },

  /** 업체 소유 새 현장 생성 + 연결(기관) — 초안 요청이면 `REQUESTED`이고 `siteId`가 null이다 */
  createSite: async (organizationId: string, body: CreateAgencySiteRequest): Promise<AgencyLinkOutcome> => {
    const response = await api.post<ApiResponse<AgencyLinkOutcome>>(
      `${base(organizationId)}/agency-links/sites`,
      body,
    );
    return response.data.data!;
  },

  /** 현장의 대행 링크(현재·과거) — 소유 조직이면 전부, 대행 기관이면 자기 링크만 */
  listSiteLinks: async (organizationId: string, siteId: string): Promise<SiteAgencyLinksResponse> => {
    const response = await api.get<ApiResponse<SiteAgencyLinksResponse>>(
      `${base(organizationId)}/sites/${encodeURIComponent(siteId)}/agency-links`,
    );
    return response.data.data!;
  },

  /** 해제 — 업체가 부르면 `REVOKED_BY_OWNER` · 기관이 부르면 `CONTRACT_ENDED` */
  unlink: async (organizationId: string, siteId: string, linkId: string): Promise<UnlinkAgencyLinkResponse> => {
    const response = await api.delete<ApiResponse<UnlinkAgencyLinkResponse>>(
      `${base(organizationId)}/sites/${encodeURIComponent(siteId)}/agency-links/${encodeURIComponent(linkId)}`,
    );
    return response.data.data!;
  },

  /** 요청 목록 — `incoming`(업체가 승인·거절할 것) · `outgoing`(기관이 보낸 것). `status` 생략 = 서버 기본 `PENDING` */
  listRequests: async (
    organizationId: string,
    direction: 'incoming' | 'outgoing',
    status?: AgencyLinkRequestStatus,
  ): Promise<AgencyLinkRequestsResponse> => {
    const params = new URLSearchParams({ direction });
    if (status) params.set('status', status);
    const response = await api.get<ApiResponse<AgencyLinkRequestsResponse>>(
      `${base(organizationId)}/agency-link-requests?${params.toString()}`,
    );
    return response.data.data!;
  },

  /** 요청 단건 — 알림 목적지(`/agency-link-requests/:requestId`)가 읽는다 */
  getRequest: async (organizationId: string, requestId: string): Promise<AgencyLinkRequestDetailResponse> => {
    const response = await api.get<ApiResponse<AgencyLinkRequestDetailResponse>>(
      `${base(organizationId)}/agency-link-requests/${encodeURIComponent(requestId)}`,
    );
    return response.data.data!;
  },

  /** 승인(업체 OWNER) — 초안 요청이면 이 순간 업체 소유 현장이 생긴다 */
  approve: async (organizationId: string, requestId: string): Promise<ApproveAgencyLinkRequestResponse> => {
    const response = await api.post<ApiResponse<ApproveAgencyLinkRequestResponse>>(
      `${base(organizationId)}/agency-link-requests/${encodeURIComponent(requestId)}/approve`,
      {},
    );
    return response.data.data!;
  },

  /** 거절(업체 OWNER) */
  reject: async (organizationId: string, requestId: string): Promise<ResolveAgencyLinkRequestResponse> => {
    const response = await api.post<ApiResponse<ResolveAgencyLinkRequestResponse>>(
      `${base(organizationId)}/agency-link-requests/${encodeURIComponent(requestId)}/reject`,
      {},
    );
    return response.data.data!;
  },

  /** 취소(기관 · `site.manage`) */
  cancel: async (organizationId: string, requestId: string): Promise<ResolveAgencyLinkRequestResponse> => {
    const response = await api.post<ApiResponse<ResolveAgencyLinkRequestResponse>>(
      `${base(organizationId)}/agency-link-requests/${encodeURIComponent(requestId)}/cancel`,
      {},
    );
    return response.data.data!;
  },
};
